import { AxiosInstance, AxiosRequestConfig } from 'axios'

import { baseService } from './base.service'
import { IBaseService } from './interfaces/base-service.interface'

import { Channel, GroupChannel } from '@/types/group-channel.type'

class GroupService {
  private static instance: GroupService
  private readonly api: AxiosInstance

  private constructor(private readonly baseService: IBaseService) {
    this.api = this.baseService.getClient()
  }

  public static getInstance(baseService: IBaseService): GroupService {
    if (!GroupService.instance) {
      GroupService.instance = new GroupService(baseService)
    }
    return GroupService.instance
  }

  // ------------------------------------Groups------------------------------------

  getGroups = async (options?: AxiosRequestConfig) => {
    const { data } = await this.api.get<GroupChannel[]>('/groups', options)
    return data
  }

  getGroupDetail = async (groupId: string, options?: AxiosRequestConfig) => {
    const { data } = await this.api.get<GroupChannel>(`/groups/${groupId}`, options)
    return data
  }

  createGroup = async (
    group: { name: string; status: 'PUBLIC' | 'PRIVATE' | 'INACTIVE'; order?: number },
    options?: AxiosRequestConfig
  ) => {
    const { data } = await this.api.post<GroupChannel>('/groups', group, options)
    return data
  }

  updateGroup = async (
    groupId: string,
    group: Partial<{ name: string; status: 'PUBLIC' | 'PRIVATE' | 'INACTIVE'; order: number }>,
    options?: AxiosRequestConfig
  ) => {
    const { data } = await this.api.put<GroupChannel>(`/groups/${groupId}`, group, options)
    return data
  }

  deleteGroup = async (groupId: string, options?: AxiosRequestConfig) => {
    const { data } = await this.api.delete(`/groups/${groupId}`, options)
    return data
  }

  reorderGroups = async (payload: { groups: { id: string; order: number }[] }, options?: AxiosRequestConfig) => {
    const { data } = await this.api.put('/groups/reorder', payload, options)
    return data
  }

  reorderChannels = async (payload: { channels: { id: string; order: number }[] }, options?: AxiosRequestConfig) => {
    const { data } = await this.api.put('/channels/reorder', payload, options)
    return data
  }

  //-------------------------- Channels-------------------------------------

  getChannels = async (options?: AxiosRequestConfig) => {
    const { data } = await this.api.get<Channel[]>(`/channels`, options)
    return data
  }

  getChannelsByGroup = async (groupId: string, options?: AxiosRequestConfig) => {
    const { data } = await this.api.get<Channel[]>(`/channels/group/${groupId}`, options)
    return data
  }

  createChannel = async (
    channel: { name: string; description?: string; status?: 'ACTIVE' | 'INACTIVE'; groupId?: string },
    options?: AxiosRequestConfig
  ) => {
    const { data } = await this.api.post<Channel>('/channels', channel, options)
    return data
  }

  updateChannel = async (
    channelId: string,
    channel: Partial<{ name: string; description?: string; status: 'ACTIVE' | 'INACTIVE'; groupId?: string }>,
    options?: AxiosRequestConfig
  ) => {
    const { data } = await this.api.put<Channel>(`/channels/${channelId}`, channel, options)
    return data
  }

  deleteChannel = async (channelId: string, options?: AxiosRequestConfig) => {
    const { data } = await this.api.delete(`/channels/${channelId}`, options)
    return data
  }
}

const groupService = GroupService.getInstance(baseService)

export default groupService
